import React from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";

const iconByName = (name = "") => {
  const normalized = name.toLowerCase();
  if (normalized.indexOf("admin") >= 0) return "e-icons e-settings";
  if (normalized.indexOf("page") >= 0 || normalized.indexOf("gest") >= 0) {
    return "e-icons e-file-new";
  }
  if (normalized.indexOf("api") >= 0 || normalized.indexOf("developer") >= 0) {
    return "e-icons e-code-view";
  }
  return "e-icons e-group-2";
};

const buildTree = (items) => {
  const nodes = new Map();

  items.forEach((item) => {
    nodes.set(String(item.id), {
      ...item,
      id: String(item.id),
      pid: item.pid !== undefined && item.pid !== null ? String(item.pid) : null,
      children: [],
    });
  });

  const roots = [];
  nodes.forEach((node) => {
    if (node.pid && nodes.has(node.pid)) {
      nodes.get(node.pid).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list) => {
    list.sort((a, b) => {
      const sortA = a.sort || a.name || "";
      const sortB = b.sort || b.name || "";
      return sortA > sortB ? 1 : sortA < sortB ? -1 : 0;
    });
    list.forEach((node) => sortNodes(node.children));
  };

  sortNodes(roots);
  return roots;
};

/* Recorre el árbol buscando el nodo activo y devuelve los IDs de sus ancestros */
const findActiveAncestors = (nodes, currentPath, ancestors = []) => {
  for (const node of nodes) {
    const trail = [...ancestors, node.id];
    if (node.link && (node.link === currentPath || currentPath.startsWith(node.link + "/"))) {
      return new Set(trail);
    }
    if (node.children.length > 0) {
      const found = findActiveAncestors(node.children, currentPath, trail);
      if (found) return found;
    }
  }
  return null;
};

const UserMenuTree = ({ dataSource = [], nodeClicked, userid, name }) => {
  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const activeItemRef = React.useRef(null);

  /* scroll sidebar to active item whenever the route changes */
  React.useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [currentPath]);

  const data = React.useMemo(() => {
    const visibleData = userid !== "0"
      ? dataSource.filter((item) => item.id !== 22)
      : dataSource;
    return buildTree(visibleData);
  }, [dataSource, userid]);

  const activeAncestors = React.useMemo(
    () => findActiveAncestors(data, currentPath) || new Set(),
    [data, currentPath]
  );

  const defaultOpen = React.useMemo(() => {
    const open = {};
    // Por defecto TODO colapsado salvo la rama de la ruta activa (menos cargado).
    // El usuario puede expandir el resto manualmente.
    activeAncestors.forEach((id) => { open[id] = true; });
    return open;
  }, [activeAncestors]);

  const [openItems, setOpenItems] = React.useState(defaultOpen);

  React.useEffect(() => {
    setOpenItems(defaultOpen);
  }, [defaultOpen]);

  const handleClick = (node) => {
    if (node.children.length > 0) {
      setOpenItems((current) => ({
        ...current,
        [node.id]: !current[node.id],
      }));
      return;
    }
    nodeClicked(node.id);
  };

  /* ── Tooltip de nombre completo (solo si el label está truncado) ──────────
     Se renderiza por portal a <body> porque el sidebar tiene transform +
     overflow (recortaría un tooltip posicionado dentro). Aparece al lado. */
  const [tip, setTip] = React.useState(null);

  const showTip = (e, text) => {
    const labelEl = e.currentTarget.querySelector(".xflow-menu-label");
    // solo cuando el texto no entra (está truncado con ellipsis)
    if (!labelEl || labelEl.scrollWidth <= labelEl.clientWidth + 1) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ text, top: r.top + r.height / 2, left: r.right + 12 });
  };
  const hideTip = () => setTip(null);

  const renderNodeContent = (node, label, hasChildren) => {
    const badgeClass = node.badgeClass
      ? "xflow-menu-count " + node.badgeClass
      : "xflow-menu-count";
    return (
    <>
      <span className={"xflow-menu-icon " + iconByName(node.name)}></span>
      <span className="xflow-menu-label">{label}</span>
      {node.actives >= 0 && (
        <span className={badgeClass}>{node.actives}</span>
      )}
      {hasChildren && (
        <span
          className={
            "xflow-menu-caret e-icons " +
            (openItems[node.id] ? "e-chevron-up" : "e-chevron-down")
          }
        ></span>
      )}
    </>
    );
  };

  const renderNode = (node, level = 0) => {
    const label = node.label || node.name;
    const hasChildren = node.children.length > 0;
    const isRoot = node.type === "root" || level === 0;
    const isActive =
      !hasChildren &&
      node.link &&
      (node.link === currentPath || currentPath.startsWith(node.link + "/"));

    /* Leaf con link real → <a> para soportar Ctrl+click (nueva pestaña) */
    /* navlink carries the full URL (with query params); link is the clean path
       used only for active-state detection (matches pathname without search). */
    const hrefTarget = node.navlink || node.link;
    const trigger =
      !hasChildren && node.link ? (
        <a
          ref={isActive ? activeItemRef : null}
          href={hrefTarget}
          className={"xflow-menu-item" + (isActive ? " xflow-menu-item--active" : "")}
          aria-current={isActive ? "page" : undefined}
          onMouseEnter={(e) => showTip(e, node.tooltip || label)}
          onMouseLeave={hideTip}
          onClick={(e) => {
            // Ctrl/Cmd/Shift/middle-click → dejar que el navegador use el href
            // (abrir en pestaña nueva).
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            hideTip();
            // Enrutar por nodeClicked (loadPage*) → navega Y cierra el menú en
            // móvil (closeSidebarOnMobile). Fallback a navigate directo.
            if (typeof nodeClicked === "function") nodeClicked(node.id);
            else navigate(hrefTarget);
          }}
        >
          {renderNodeContent(node, label, false)}
        </a>
      ) : (
        <button
          className={"xflow-menu-item" + (activeAncestors.has(node.id) && hasChildren ? " xflow-menu-item--active-parent" : "")}
          onClick={() => handleClick(node)}
          onMouseEnter={(e) => showTip(e, node.tooltip || label)}
          onMouseLeave={hideTip}
          type="button"
        >
          {renderNodeContent(node, label, hasChildren)}
        </button>
      );

    return (
      <li
        className={
          "xflow-menu-node" +
          (isRoot ? " xflow-menu-node-root" : "") +
          (hasChildren ? " xflow-menu-node-parent" : "")
        }
        key={node.id}
      >
        {trigger}
        {hasChildren && openItems[node.id] && (
          <ul className="xflow-menu-children">
            {node.children.map((child) => renderNode(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  if (data.length === 0) {
    return null;
  }

  return (
    <nav className="xflow-menu-tree" data-menu-name={name}>
      <ul>{data.map((node) => renderNode(node))}</ul>
      {tip &&
        ReactDOM.createPortal(
          <div
            className="xflow-menu-tooltip"
            style={{ top: tip.top, left: tip.left }}
            role="tooltip"
          >
            {tip.text}
          </div>,
          document.body
        )}
    </nav>
  );
};

export default UserMenuTree;
